<template>
  <div class="customers-page">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-users"></i>
          Gestion des Clients
        </h1>
        <div class="header-actions">
          <button 
            class="btn btn-primary"
            @click="showAddModal = true"
          >
            <i class="fas fa-plus"></i>
            Nouveau Client
          </button>
          <button 
            class="btn btn-secondary"
            @click="exportCustomers"
            :disabled="loading"
          >
            <i class="fas fa-download"></i>
            Exporter
          </button>
        </div>
      </div>
    </div>

    <!-- Statistiques -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-users"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatCount(customersStore.stats.total) }}</div>
          <div class="stat-label">Total Clients</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon active">
          <i class="fas fa-user-check"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatCount(customersStore.stats.active) }}</div>
          <div class="stat-label">Clients Actifs</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon orders">
          <i class="fas fa-shopping-cart"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatCount(customersStore.stats.withOrders) }}</div>
          <div class="stat-label">Avec Commandes</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon revenue">
          <i class="fas fa-euro-sign"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatCurrency(customersStore.stats.totalRevenue) }}</div>
          <div class="stat-label">CA Total</div>
        </div>
      </div>
    </div>

    <!-- Filtres -->
    <div class="filters-section">
      <div class="filters-row">
        <div class="filter-group">
          <label>Recherche</label>
          <div class="search-input">
            <i class="fas fa-search"></i>
            <input 
              type="text" 
              v-model="searchQuery"
              placeholder="Nom, email, entreprise..."
              @input="debouncedSearch"
            >
          </div>
        </div>
        <div class="filter-group">
          <label>Statut</label>
          <select v-model="filters.status" @change="applyFilters">
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
            <option value="prospect">Prospect</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Type</label>
          <select v-model="filters.type" @change="applyFilters">
            <option value="">Tous les types</option>
            <option value="individual">Particulier</option>
            <option value="company">Entreprise</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Pays</label>
          <select v-model="filters.country" @change="applyFilters">
            <option value="">Tous les pays</option>
            <option value="FR">France</option>
            <option value="BE">Belgique</option>
            <option value="CH">Suisse</option>
            <option value="DE">Allemagne</option>
          </select>
        </div>
        <button 
          class="btn btn-outline"
          @click="clearFilters"
          v-if="hasActiveFilters"
        >
          <i class="fas fa-times"></i>
          Effacer
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Chargement des clients...</p>
    </div>

    <!-- Liste des clients -->
    <div v-else class="customers-table-container">
      <div class="table-responsive">
      <table class="customers-table">
        <thead>
          <tr>
            <th @click="sortBy('name')" class="sortable">
              Nom/Entreprise
              <i class="fas fa-sort" :class="getSortIcon('name')"></i>
            </th>
            <th @click="sortBy('email')" class="sortable">
              Email
              <i class="fas fa-sort" :class="getSortIcon('email')"></i>
            </th>
            <th>Contact</th>
            <th>Adresse</th>
            <th @click="sortBy('status')" class="sortable">
              Statut
              <i class="fas fa-sort" :class="getSortIcon('status')"></i>
            </th>
            <th @click="sortBy('ordersCount')" class="sortable">
              Commandes
              <i class="fas fa-sort" :class="getSortIcon('ordersCount')"></i>
            </th>
            <th @click="sortBy('totalRevenue')" class="sortable">
              CA Total
              <i class="fas fa-sort" :class="getSortIcon('totalRevenue')"></i>
            </th>
            <th @click="sortBy('lastOrderDate')" class="sortable">
              Dernière Commande
              <i class="fas fa-sort" :class="getSortIcon('lastOrderDate')"></i>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="customer in filteredCustomers" :key="customer.id">
            <td>
              <div class="customer-info">
                <div class="customer-avatar">
                  <i class="fas" :class="customer.type === 'company' ? 'fa-building' : 'fa-user'"></i>
                </div>
                <div class="customer-details">
                  <div class="customer-name">{{ customer.name }}</div>
                  <div class="customer-type">{{ customer.thirdParty ? getThirdPartyLabel(customer.thirdParty) : getTypeLabel(customer.type) }}</div>
                </div>
              </div>
            </td>
            <td>
              <a v-if="customer.email" :href="`mailto:${customer.email}`" class="email-link">
                {{ customer.email }}
              </a>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <div v-if="customer.phone" class="contact-info">
                <div>{{ customer.phone }}</div>
                <div v-if="customer.contactPerson" class="contact-person">
                  {{ customer.contactPerson }}
                </div>
              </div>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <div v-if="customer.address" class="address-info">
                <div>{{ customer.address.street }}</div>
                <div>{{ customer.address.postalCode }} {{ customer.address.city }}</div>
                <div>{{ customer.address.country }}</div>
              </div>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span class="status-badge" :class="customer.status">
                {{ getStatusLabel(customer.status) }}
              </span>
            </td>
            <td class="text-center">
              <span class="orders-count">{{ formatCount(customer.ordersCount) }}</span>
            </td>
            <td class="text-right">
              <span class="revenue-amount">{{ formatCurrency(customer.totalRevenue) }}</span>
            </td>
            <td>
              <span v-if="customer.lastOrderDate" class="last-order-date">
                {{ formatDate(customer.lastOrderDate) }}
              </span>
              <span v-else class="text-muted">{{ customer.ordersCount === 0 ? 'Jamais' : '—' }}</span>
            </td>
            <td>
              <div class="actions-menu">
                <button 
                  class="btn btn-sm btn-outline"
                  @click="viewCustomer(customer)"
                  title="Voir"
                  aria-label="Voir"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button 
                  class="btn btn-sm btn-outline"
                  @click="editCustomer(customer)"
                  title="Modifier"
                  aria-label="Modifier"
                >
                  <i class="fas fa-edit"></i>
                </button>
                <button 
                  class="btn btn-sm btn-outline btn-danger"
                  @click="confirmDelete(customer)"
                  title="Supprimer"
                  aria-label="Supprimer"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-container" v-if="customersStore.pagination.total > customersStore.pagination.limit">
        <div class="pagination-info">
          Affichage de {{ (customersStore.pagination.page - 1) * customersStore.pagination.limit + 1 }} à 
          {{ Math.min(customersStore.pagination.page * customersStore.pagination.limit, customersStore.pagination.total) }} 
          sur {{ customersStore.pagination.total }} clients
        </div>
        <div class="pagination-controls">
          <button 
            class="btn btn-sm btn-outline"
            @click="changePage(customersStore.pagination.page - 1)"
            :disabled="customersStore.pagination.page === 1"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="page-info">
            Page {{ customersStore.pagination.page }} sur {{ totalPages }}
          </span>
          <button 
            class="btn btn-sm btn-outline"
            @click="changePage(customersStore.pagination.page + 1)"
            :disabled="customersStore.pagination.page === totalPages"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Ajout/Modification -->
    <Teleport to="body">
    <div v-if="showAddModal || showEditModal" class="modal-overlay" @click="closeModals">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ showAddModal ? 'Nouveau Client' : 'Modifier Client' }}</h3>
          <button class="modal-close" @click="closeModals" aria-label="Fermer">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveCustomer">
            <div class="form-row">
              <div class="form-group">
                <label>Type *</label>
                <select v-model="customerForm.type" required>
                  <option value="individual">Particulier</option>
                  <option value="company">Entreprise</option>
                </select>
              </div>
              <div class="form-group">
                <label>Statut *</label>
                <select v-model="customerForm.status" required>
                  <option value="prospect">Prospect</option>
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
            </div>
            
            <div class="form-group">
              <label>{{ customerForm.type === 'company' ? 'Nom de l\'entreprise' : 'Nom complet' }} *</label>
              <input 
                type="text" 
                v-model="customerForm.name" 
                required
                :placeholder="customerForm.type === 'company' ? 'Ex: ACME Corp' : 'Ex: Jean Dupont'"
              >
            </div>
            
            <div class="form-group">
              <label>Email *</label>
              <input type="email" v-model="customerForm.email" required>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Téléphone</label>
                <input type="tel" v-model="customerForm.phone">
              </div>
              <div class="form-group" v-if="customerForm.type === 'company'">
                <label>Personne de contact</label>
                <input type="text" v-model="customerForm.contactPerson">
              </div>
            </div>
            
            <div class="form-section">
              <h4>Adresse</h4>
              <div class="form-group">
                <label>Rue</label>
                <input type="text" v-model="customerForm.address.street">
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Code postal</label>
                  <input type="text" v-model="customerForm.address.postalCode">
                </div>
                <div class="form-group">
                  <label>Ville</label>
                  <input type="text" v-model="customerForm.address.city">
                </div>
              </div>
              <div class="form-group">
                <label>Pays</label>
                <select v-model="customerForm.address.country">
                  <option value="FR">France</option>
                  <option value="BE">Belgique</option>
                  <option value="CH">Suisse</option>
                  <option value="DE">Allemagne</option>
                  <option value="ES">Espagne</option>
                  <option value="IT">Italie</option>
                </select>
              </div>
            </div>
            
            <div class="form-group">
              <label>Notes</label>
              <textarea v-model="customerForm.notes" rows="3"></textarea>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModals">
            Annuler
          </button>
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="saveCustomer"
            :disabled="saving"
          >
            <i v-if="saving" class="fas fa-spinner fa-spin"></i>
            {{ saving ? 'Enregistrement...' : (showAddModal ? 'Créer' : 'Modifier') }}
          </button>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- Modal Détails -->
    <Teleport to="body">
    <div v-if="showDetailsModal" class="modal-overlay" @click="closeModals">
      <div class="modal-content modal-large" @click.stop>
        <div class="modal-header">
          <h3>Détails du Client</h3>
          <button class="modal-close" @click="closeModals" aria-label="Fermer">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body" v-if="selectedCustomer">
          <div class="customer-details-grid">
            <div class="details-section">
              <h4>Informations générales</h4>
              <div class="detail-item">
                <label>Nom/Entreprise:</label>
                <span>{{ selectedCustomer.name }}</span>
              </div>
              <div class="detail-item">
                <label>Type:</label>
                <span>{{ selectedCustomer.thirdParty ? getThirdPartyLabel(selectedCustomer.thirdParty) : getTypeLabel(selectedCustomer.type) }}</span>
              </div>
              <div class="detail-item">
                <label>Statut:</label>
                <span class="status-badge" :class="selectedCustomer.status">
                  {{ getStatusLabel(selectedCustomer.status) }}
                </span>
              </div>
              <div class="detail-item">
                <label>Email:</label>
                <a v-if="selectedCustomer.email" :href="`mailto:${selectedCustomer.email}`">{{ selectedCustomer.email }}</a>
                <span v-else class="text-muted">—</span>
              </div>
              <div class="detail-item" v-if="selectedCustomer.phone">
                <label>Téléphone:</label>
                <span>{{ selectedCustomer.phone }}</span>
              </div>
              <div class="detail-item" v-if="selectedCustomer.contactPerson">
                <label>Contact:</label>
                <span>{{ selectedCustomer.contactPerson }}</span>
              </div>
            </div>
            
            <div class="details-section" v-if="selectedCustomer.address">
              <h4>Adresse</h4>
              <div class="address-display">
                <div>{{ selectedCustomer.address.street }}</div>
                <div>{{ selectedCustomer.address.postalCode }} {{ selectedCustomer.address.city }}</div>
                <div>{{ selectedCustomer.address.country }}</div>
              </div>
            </div>
            
            <div class="details-section">
              <h4>Statistiques</h4>
              <div class="stats-row">
                <div class="stat-item">
                  <div class="stat-value">{{ formatCount(selectedCustomer.ordersCount) }}</div>
                  <div class="stat-label">Commandes</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value">{{ formatCurrency(selectedCustomer.totalRevenue) }}</div>
                  <div class="stat-label">CA Total</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value">{{ formatCurrency(selectedCustomer.averageOrderValue) }}</div>
                  <div class="stat-label">Panier Moyen</div>
                </div>
              </div>
            </div>
            
            <div class="details-section" v-if="selectedCustomer.notes">
              <h4>Notes</h4>
              <p>{{ selectedCustomer.notes }}</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModals">
            Fermer
          </button>
          <button class="btn btn-primary" @click="editCustomer(selectedCustomer)">
            <i class="fas fa-edit"></i>
            Modifier
          </button>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- Modal Confirmation Suppression -->
    <Teleport to="body">
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeModals">
      <div class="modal-content modal-small" @click.stop>
        <div class="modal-header">
          <h3>Confirmer la suppression</h3>
          <button class="modal-close" @click="closeModals" aria-label="Fermer">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Êtes-vous sûr de vouloir supprimer le client <strong>{{ customerToDelete?.name }}</strong> ?</p>
          <p class="text-warning">Cette action est irréversible.</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModals">
            Annuler
          </button>
          <button 
            class="btn btn-danger" 
            @click="deleteCustomer"
            :disabled="deleting"
          >
            <i v-if="deleting" class="fas fa-spinner fa-spin"></i>
            {{ deleting ? 'Suppression...' : 'Supprimer' }}
          </button>
        </div>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useCustomersStore } from '@/stores/customers'
import { debounce } from 'lodash'

export default {
  name: 'Customers',
  setup() {
    const customersStore = useCustomersStore()
    
    // État local
    const loading = ref(false)
    const saving = ref(false)
    const deleting = ref(false)
    const searchQuery = ref('')
    const sortField = ref('name')
    const sortDirection = ref('asc')
    
    // Modals
    const showAddModal = ref(false)
    const showEditModal = ref(false)
    const showDetailsModal = ref(false)
    const showDeleteModal = ref(false)
    
    // Données sélectionnées
    const selectedCustomer = ref(null)
    const customerToDelete = ref(null)
    
    // Filtres
    const filters = ref({
      status: '',
      type: '',
      country: ''
    })
    
    // Formulaire client
    const customerForm = ref({
      name: '',
      email: '',
      phone: '',
      contactPerson: '',
      type: 'individual',
      status: 'prospect',
      address: {
        street: '',
        postalCode: '',
        city: '',
        country: 'FR'
      },
      notes: ''
    })
    
    // Computed
    const filteredCustomers = computed(() => {
      let customers = [...customersStore.customers]
      
      // Recherche textuelle
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        customers = customers.filter(customer => 
          (customer.name || '').toLowerCase().includes(query) ||
          (customer.email || '').toLowerCase().includes(query) ||
          (customer.lupCode || '').toLowerCase().includes(query) ||
          (customer.contactPerson && customer.contactPerson.toLowerCase().includes(query))
        )
      }
      
      // Filtres
      if (filters.value.status) {
        customers = customers.filter(c => c.status === filters.value.status)
      }
      if (filters.value.type) {
        customers = customers.filter(c => c.type === filters.value.type)
      }
      if (filters.value.country) {
        customers = customers.filter(c => c.address?.country === filters.value.country)
      }
      
      // Tri
      customers.sort((a, b) => {
        let aVal = a[sortField.value]
        let bVal = b[sortField.value]
        
        if (sortField.value === 'lastOrderDate') {
          aVal = aVal ? new Date(aVal) : new Date(0)
          bVal = bVal ? new Date(bVal) : new Date(0)
        }
        
        // Valeurs absentes (champ sans source API) toujours en fin de liste
        const aMissing = aVal === null || aVal === undefined
        const bMissing = bVal === null || bVal === undefined
        if (aMissing || bMissing) return aMissing === bMissing ? 0 : (aMissing ? 1 : -1)
        
        if (typeof aVal === 'string') aVal = aVal.toLowerCase()
        if (typeof bVal === 'string') bVal = bVal.toLowerCase()
        
        if (sortDirection.value === 'asc') {
          return aVal > bVal ? 1 : -1
        } else {
          return aVal < bVal ? 1 : -1
        }
      })
      
      return customers
    })
    
    const hasActiveFilters = computed(() => {
      return filters.value.status || filters.value.type || filters.value.country || searchQuery.value
    })
    
    const totalPages = computed(() => {
      return Math.ceil(customersStore.pagination.total / customersStore.pagination.limit)
    })
    
    // Méthodes
    const loadCustomers = async () => {
      loading.value = true
      try {
        await customersStore.loadCustomers()
      } finally {
        loading.value = false
      }
    }
    
    const debouncedSearch = debounce(() => {
      // La recherche est gérée par le computed filteredCustomers
    }, 300)
    
    const sortBy = (field) => {
      if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortField.value = field
        sortDirection.value = 'asc'
      }
    }
    
    const getSortIcon = (field) => {
      if (sortField.value !== field) return ''
      return sortDirection.value === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
    }
    
    const applyFilters = () => {
      // Les filtres sont appliqués automatiquement via le computed
    }
    
    const clearFilters = () => {
      filters.value = {
        status: '',
        type: '',
        country: ''
      }
      searchQuery.value = ''
    }
    
    const changePage = (page) => {
      customersStore.setPagination(page)
      loadCustomers()
    }
    
    const viewCustomer = (customer) => {
      selectedCustomer.value = customer
      showDetailsModal.value = true
    }
    
    const editCustomer = (customer) => {
      customerForm.value = {
        ...customer,
        address: { ...customer.address }
      }
      selectedCustomer.value = customer
      showEditModal.value = true
      showDetailsModal.value = false
    }
    
    const confirmDelete = (customer) => {
      customerToDelete.value = customer
      showDeleteModal.value = true
    }
    
    const saveCustomer = async () => {
      saving.value = true
      try {
        if (showAddModal.value) {
          await customersStore.createCustomer(customerForm.value)
        } else {
          await customersStore.updateCustomer(selectedCustomer.value.id, customerForm.value)
        }
        closeModals()
        await loadCustomers()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      } finally {
        saving.value = false
      }
    }
    
    const deleteCustomer = async () => {
      deleting.value = true
      try {
        await customersStore.deleteCustomer(customerToDelete.value.id)
        closeModals()
        await loadCustomers()
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
      } finally {
        deleting.value = false
      }
    }
    
    const exportCustomers = async () => {
      try {
        await customersStore.exportCustomers('csv', filters.value)
      } catch (error) {
        console.error('Erreur lors de l\'export:', error)
      }
    }
    
    const closeModals = () => {
      showAddModal.value = false
      showEditModal.value = false
      showDetailsModal.value = false
      showDeleteModal.value = false
      selectedCustomer.value = null
      customerToDelete.value = null
      
      // Reset form
      customerForm.value = {
        name: '',
        email: '',
        phone: '',
        contactPerson: '',
        type: 'individual',
        status: 'prospect',
        address: {
          street: '',
          postalCode: '',
          city: '',
          country: 'FR'
        },
        notes: ''
      }
    }
    
    // Utilitaires
    // « — » quand la valeur n'existe pas dans l'API (jamais de NaN / vide)
    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined || amount === '') return '—'
      const value = Number(amount)
      if (!Number.isFinite(value)) return '—'
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(value)
    }
    
    const formatCount = (value) => {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      return Number.isFinite(n) ? n.toLocaleString('fr-FR') : '—'
    }
    
    const formatDate = (date) => {
      if (!date) return '—'
      const d = new Date(date)
      return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR')
    }
    
    const getStatusLabel = (status) => {
      const labels = {
        active: 'Actif',
        inactive: 'Inactif',
        prospect: 'Prospect'
      }
      if (!status) return '—'
      return labels[status] || status
    }
    
    // Champ API `third_party` (THIRD / Intercompany)
    const getThirdPartyLabel = (thirdParty) => {
      const labels = {
        THIRD: 'Tiers',
        Intercompany: 'Intercompany'
      }
      return labels[thirdParty] || thirdParty
    }
    
    const getTypeLabel = (type) => {
      const labels = {
        individual: 'Particulier',
        company: 'Entreprise'
      }
      return labels[type] || type
    }
    
    // Lifecycle
    onMounted(() => {
      loadCustomers()
    })
    
    return {
      customersStore,
      loading,
      saving,
      deleting,
      searchQuery,
      sortField,
      sortDirection,
      showAddModal,
      showEditModal,
      showDetailsModal,
      showDeleteModal,
      selectedCustomer,
      customerToDelete,
      filters,
      customerForm,
      filteredCustomers,
      hasActiveFilters,
      totalPages,
      loadCustomers,
      debouncedSearch,
      sortBy,
      getSortIcon,
      applyFilters,
      clearFilters,
      changePage,
      viewCustomer,
      editCustomer,
      confirmDelete,
      saveCustomer,
      deleteCustomer,
      exportCustomers,
      closeModals,
      formatCurrency,
      formatCount,
      formatDate,
      getStatusLabel,
      getThirdPartyLabel,
      getTypeLabel
    }
  }
}
</script>

<style scoped>
.customers-page {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  background: var(--primary-color);
}

.stat-icon.active {
  background: var(--success-color);
}

.stat-icon.orders {
  background: var(--warning-color);
}

.stat-icon.revenue {
  background: var(--info-color);
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
}

.stat-label {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.filters-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filters-row {
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 150px;
}

.filter-group label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.search-input {
  position: relative;
}

.search-input i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
}

.search-input input {
  padding-left: 2.5rem;
  width: 250px;
}

.customers-table-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.customers-table {
  width: 100%;
  border-collapse: collapse;
}

.customers-table th {
  background: var(--gray-50);
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.customers-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.customers-table th.sortable:hover {
  background: var(--gray-100);
}

.customers-table th i {
  margin-left: 0.5rem;
  opacity: 0.5;
}

.customers-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  vertical-align: top;
}

.customer-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.customer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.125rem;
}

.customer-name {
  font-weight: 500;
  color: var(--text-primary);
}

.customer-type {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.email-link {
  color: var(--primary-color);
  text-decoration: none;
}

.email-link:hover {
  text-decoration: underline;
}

.contact-info {
  font-size: 0.875rem;
}

.contact-person {
  color: var(--text-secondary);
  font-size: 0.75rem;
}

.address-info {
  font-size: 0.875rem;
  line-height: 1.4;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.status-badge.active {
  background: var(--success-light);
  color: var(--success-color);
}

.status-badge.inactive {
  background: var(--gray-100);
  color: var(--text-secondary);
}

.status-badge.prospect {
  background: var(--warning-light);
  color: var(--warning-color);
}

.orders-count {
  font-weight: 500;
}

.revenue-amount {
  font-weight: 500;
  color: var(--success-color);
}

.last-order-date {
  font-size: 0.875rem;
}

.actions-menu {
  display: flex;
  gap: 0.25rem;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--border-color);
}

.pagination-info {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page-info {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.customer-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.details-section h4 {
  margin-bottom: 1rem;
  color: var(--text-primary);
  font-weight: 600;
}

.detail-item {
  display: flex;
  margin-bottom: 0.75rem;
}

.detail-item label {
  font-weight: 500;
  color: var(--text-secondary);
  min-width: 120px;
}

.address-display {
  line-height: 1.5;
}

.stats-row {
  display: flex;
  gap: 2rem;
}

.stat-item {
  text-align: center;
}

.stat-item .stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--primary-color);
}

.stat-item .stat-label {
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

.form-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
}

.form-section h4 {
  margin-bottom: 1rem;
  color: var(--text-primary);
}

/* Modales maison (overlay + panneau) */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1060;
}

.modal-overlay .modal-content {
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
  border-radius: 12px;
}

.modal-overlay .modal-large {
  max-width: 900px;
}

.modal-overlay .modal-small {
  max-width: 440px;
}

.modal-overlay .modal-header,
.modal-overlay .modal-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  flex-shrink: 0;
}

.modal-overlay .modal-header {
  justify-content: space-between;
  border-bottom: 1px solid var(--border-color);
}

.modal-overlay .modal-header h3 {
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
}

.modal-overlay .modal-footer {
  justify-content: flex-end;
  flex-wrap: wrap;
  border-top: 1px solid var(--border-color);
}

.modal-overlay .modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  flex-shrink: 0;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
  min-width: 0;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  max-width: 100%;
}

/* Textes longs : jamais de débordement */
.customer-info,
.customer-details,
.detail-item {
  min-width: 0;
}

.customer-avatar {
  flex-shrink: 0;
}

.customer-name,
.email-link,
.address-info,
.detail-item span,
.detail-item a,
.address-display,
.details-section p {
  overflow-wrap: anywhere;
}

.detail-item {
  gap: 0.5rem;
}

.customer-details-grid {
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
}

.stats-row {
  flex-wrap: wrap;
}

.status-badge {
  display: inline-block;
  white-space: nowrap;
}

.pagination-container {
  flex-wrap: wrap;
  gap: 0.75rem;
}

.header-actions {
  flex-wrap: wrap;
}

.stat-content {
  min-width: 0;
}

.stat-value {
  overflow-wrap: anywhere;
}

.text-muted {
  color: var(--text-secondary);
}

.text-warning {
  color: var(--warning-color);
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

@media (max-width: 768px) {
  .customers-page {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters-row {
    flex-direction: column;
  }
  
  .filter-group {
    min-width: auto;
  }
  
  .search-input input {
    width: 100%;
  }
  
  .customers-table-container {
    overflow-x: auto;
  }
  
  .customers-table {
    min-width: 800px;
  }
  
  .pagination-container {
    flex-direction: column;
    gap: 1rem;
  }
}

/* Écrans < 1600px : le tableau tient dans sa carte (en-têtes sur 2 lignes) */
@media (max-width: 1599.98px) {
  .customers-table th,
  .customers-table td {
    padding: 0.75rem;
  }

  .customers-table th {
    white-space: normal;
    vertical-align: bottom;
  }

  .customers-table th i {
    margin-left: 0.25rem;
  }
}

@media (max-width: 767.98px) {
  .page-title {
    font-size: clamp(1.5rem, 6.5vw, 2rem);
  }

  .header-actions .btn {
    flex: 1 1 auto;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }

  .stat-card {
    padding: 1rem;
    gap: 0.75rem;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 1.125rem;
    flex-shrink: 0;
  }

  .stat-value {
    font-size: 1.25rem;
  }

  .filters-section {
    padding: 1rem;
    margin-bottom: 1.25rem;
  }

  .filters-row {
    align-items: stretch;
  }

  .filter-group select {
    width: 100%;
  }

  .filters-row .btn {
    justify-content: center;
  }

  .customers-table {
    min-width: 760px;
  }

  .customers-table th,
  .customers-table td {
    padding: 0.75rem;
  }

  .pagination-container {
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: space-between;
  }

  /* Modales plein écran sur téléphone */
  .modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .modal-overlay .modal-content,
  .modal-overlay .modal-large {
    max-width: 100%;
    max-height: none;
    height: 100%;
    height: 100dvh;
    border-radius: 0;
  }

  .modal-overlay .modal-small {
    max-width: calc(100% - 2rem);
    height: auto;
    margin: auto;
    border-radius: 12px;
  }

  .modal-overlay .modal-header,
  .modal-overlay .modal-footer,
  .modal-overlay .modal-body {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .modal-overlay .modal-footer .btn {
    flex: 1 1 auto;
    justify-content: center;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .detail-item {
    flex-direction: column;
    gap: 0.125rem;
  }

  .detail-item label {
    min-width: 0;
  }

  .stats-row {
    gap: 1rem 1.5rem;
  }
}

@media (max-width: 575.98px) {
  .customers-page {
    padding: 0;
  }

  .page-header {
    margin-bottom: 1.25rem;
  }
}

@media (max-width: 359.98px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

@media (pointer: coarse) {
  .actions-menu .btn,
  .pagination-controls .btn,
  .modal-close {
    min-width: 44px;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>